import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CalendarBackendService } from './calendar-backend.service';

export interface CalendarEvent {
  title: string;
  description: string;
  start: string;       // ISO 8601: "2026-06-10T09:00:00"
  end: string;         // ISO 8601: "2026-06-10T10:00:00"
  location?: string;
  attendees?: string[];
  timeZone?: string;
}

@Injectable({ providedIn: 'root' })
export class OutlookCalendarService {

  constructor(
    private http: HttpClient,
    private calendarBackend: CalendarBackendService
  ) {}

  // Obtener headers con token de aplicación del backend (Client Credentials)
  private getAuthHeaders(): HttpHeaders {
    const token = this.calendarBackend.getAccessToken();

    if (!token) {
      throw new Error('No hay token de Microsoft. El login del backend no se ha completado.');
    }

    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  // URL base de Graph API usando /users/{doctor_email} (no /me, porque es token de aplicación)
  private getGraphUserUrl(): string {
    const doctorEmail = this.calendarBackend.getDoctorEmail();
    return `https://graph.microsoft.com/v1.0/users/${doctorEmail}`;
  }

  // CREAR evento en Outlook Calendar
  async createEvent(event: CalendarEvent): Promise<any> {
    const headers = this.getAuthHeaders();
    const timeZone = event.timeZone || 'America/Bogota';

    const body = {
      subject: event.title,
      body: {
        contentType: 'HTML',
        content: event.description,
      },
      start: {
        dateTime: event.start,
        timeZone: timeZone,
      },
      end: {
        dateTime: event.end,
        timeZone: timeZone,
      },
      location: {
        displayName: event.location || '',
      },
      attendees: event.attendees?.map((email) => ({
        emailAddress: {
          address: email,
          name: email,
        },
        type: 'required',
      })) || [],
    };

    return this.http
      .post(`${this.getGraphUserUrl()}/events`, body, { headers })
      .toPromise();
  }

  // LEER eventos del calendario
  async getEvents(startDate?: string, endDate?: string): Promise<any[]> {
    const headers = this.getAuthHeaders();
    let url = `${this.getGraphUserUrl()}/events?$top=50&$orderby=start/dateTime`;

    // Filtrar por rango de fechas si se proporciona
    if (startDate && endDate) {
      url += `&$filter=start/dateTime ge '${startDate}' and end/dateTime le '${endDate}'`;
    }

    const response: any = await this.http
      .get(url, { headers })
      .toPromise();
    return response.value || [];
  }

  // ACTUALIZAR evento
  async updateEvent(eventId: string, event: CalendarEvent): Promise<any> {
    const headers = this.getAuthHeaders();
    const timeZone = event.timeZone || 'America/Bogota';

    const body = {
      subject: event.title,
      body: { contentType: 'HTML', content: event.description },
      start: { dateTime: event.start, timeZone },
      end: { dateTime: event.end, timeZone },
    };

    return this.http
      .patch(`${this.getGraphUserUrl()}/events/${eventId}`, body, { headers })
      .toPromise();
  }

  // ELIMINAR evento
  async deleteEvent(eventId: string): Promise<any> {
    const headers = this.getAuthHeaders();
    return this.http
      .delete(`${this.getGraphUserUrl()}/events/${eventId}`, { headers })
      .toPromise();
  }
}
